import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Phone, ShieldCheck, Truck, Clock, MapPin } from "lucide-react";
import { site, usps } from "@/data/site";
import { services } from "@/data/services";
import { regions, regionsByGroup } from "@/data/regions";
import { faq } from "@/data/faq";
import { images } from "@/data/images";
import heroVideo from "@/assets/hero-home.mp4.asset.json";
import heroPoster from "@/assets/hero-home-poster.jpg.asset.json";

import verhuisliftFlat from "@/assets/home-verhuislift-flat.jpg.asset.json";
import vlootWagens from "@/assets/home-vloot-wagens.jpg.asset.json";
import homeVideo2 from "@/assets/home-video-2.mp4.asset.json";
import homeVideo2Poster from "@/assets/home-video-2-poster.jpg.asset.json";
import liftAppartement from "@/assets/home-verhuislift-appartement.jpg.asset.json";
import homeVideo from "@/assets/home-video-new.mp4.asset.json";
import homeVideoPoster from "@/assets/home-video-new-poster.jpg.asset.json";

import { Section, SectionHeading } from "@/components/site/Section";

import { QuoteForm } from "@/components/site/QuoteForm";
import { ReviewsBlock, ReviewPlatforms, Stars } from "@/components/site/ReviewsBlock";
import { FaqBlock } from "@/components/site/FaqBlock";
import { CtaBand } from "@/components/site/CtaBand";
import { JsonLd } from "@/components/site/JsonLd";
import { ReviewBar } from "@/components/site/ReviewBar";
import { localBusinessSchema, websiteSchema, faqSchema, meta } from "@/lib/schema";
import { overviewKeywords } from "@/lib/localKeywords";


const title = "Verhuisbedrijf Zuid-Holland | Vakkundig Verhuizingen & Transport";
const description =
  "Vakkundig Verhuizingen & Transport: betrouwbaar verhuisbedrijf in Zuid-Holland en heel Nederland. Particulier, zakelijk, verhuislift vanaf €95/uur, opslag en transport. 4,9/5 uit 507 reviews. Gratis offerte.";

export const Route = createFileRoute("/")({
  head: () =>
    meta({
      title,
      description,
      path: "/",
      image: "home",
      keywords: overviewKeywords(["verhuisbedrijf Nederland", "verhuislift huren Nederland"]),
    }),
  component: Home,
});

function Home() {
  return (
    <>
      <JsonLd data={localBusinessSchema} />
      <JsonLd data={websiteSchema} />
      <JsonLd data={faqSchema(faq)} />

      <section className="relative overflow-hidden bg-secondary text-secondary-foreground">
        <video
          src={heroVideo.url}
          poster={heroPoster.url}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label="Video van de verhuislift van Vakkundig Verhuizingen & Transport"

          className="absolute inset-0 size-full object-cover opacity-50"
        />

        <div className="container-page relative grid gap-10 pb-16 pt-24 sm:pb-24 sm:pt-28 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pt-24">
          <div>
            <h1 className="font-display text-4xl font-extrabold leading-[1.1] sm:text-5xl">

              Verhuisbedrijf dat je verhuizing door heel Nederland compleet uit handen neemt
            </h1>
            <p className="mt-5 max-w-xl text-lg opacity-90">
              Vakkundig Verhuizingen &amp; Transport is een jong, dynamisch team dat van
              aanpakken houdt vakkundig: ervaring met montage en demontage, verhuisliften en

              Inpakservice. Alles strak georganiseerd en met oog voor detail, door heel Nederland —
              particulier of zakelijk, één kamer of een compleet kantoor. Altijd persoonlijk, altijd
              doorpakken tot de laatste doos op zijn plek staat.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/offerte"
                className="rounded-md bg-brand px-6 py-3.5 font-bold text-brand-foreground transition hover:bg-brand-dark"
              >
                Gratis offerte aanvragen
              </Link>
              <a
                href={site.phoneHref}
                className="inline-flex items-center gap-2 rounded-md border border-background/30 px-6 py-3.5 font-bold transition hover:border-brand hover:text-brand"
              >
                <Phone className="size-4" aria-hidden />
                Bel {site.phone}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-semibold">
              <span className="flex items-center gap-2">
                <Stars rating={5} /> 4,9 uit 507 reviews
              </span>
              <span className="flex items-center gap-2 opacity-90">
                <ShieldCheck className="size-4 text-brand" aria-hidden /> Verzekerd vervoer
              </span>
              <span className="flex items-center gap-2 opacity-90">
                <Clock className="size-4 text-brand" aria-hidden /> 7 dagen per week
              </span>
            </div>
          </div>

          <div className="mt-6 w-full self-center rounded-2xl border border-background/20 bg-background/85 p-5 text-foreground shadow-[var(--shadow-card)] backdrop-blur-md sm:p-6 lg:mt-0 lg:ml-auto lg:max-w-[420px]">
            <h2 className="font-display text-lg font-extrabold text-secondary sm:text-xl">
              Vraag direct je offerte aan
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Vul kort je gegevens in. Je hoort binnen één werkdag wat je verhuizing kost.
            </p>
            <div className="mt-4 [&_button[type=submit]]:h-10 [&_input]:h-9 [&_input]:text-xs [&_span]:text-xs [&_textarea]:text-xs">
              <QuoteForm compact />
            </div>
          </div>

        </div>
      </section>

      <ReviewBar />

      <Section>
        <SectionHeading
          eyebrow="Onze diensten"
          title="Alles voor je verhuizing Vakkundig geregeld"
          intro="Vakkundig Verhuizingen & Transport verzorgt particuliere en zakelijke verhuizingen, seniorenverhuizingen en complete ontruimingen. Daarnaast staan we regelmatig op locatie voor evenementen: op- en afbouw en het transport van meubilair, standbouw en materialen. Van een enkel meubelstuk tot een compleet kantoor, inclusief inpakservice, verhuislift en tijdelijke opslag."

        />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <Link
              key={service.slug}
              to="/diensten/$slug"
              params={{ slug: service.slug }}
              className="group overflow-hidden rounded-xl border border-border bg-background shadow-[var(--shadow-card)] transition hover:border-brand"
            >
              <img
                src={images[service.image]}
                alt={`${service.title} door Vakkundig Verhuizingen & Transport`}
                className="h-44 w-full object-cover"
                loading="lazy"
              />
              <div className="p-6">
                <h3 className="font-display text-xl font-bold text-secondary group-hover:text-brand">
                  {service.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{service.short}</p>
                <span className="mt-4 inline-block text-sm font-bold text-brand">Meer informatie →</span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section tone="sand">
        <SectionHeading
          eyebrow="Uit de praktijk"
          title="Dagelijks onderweg door heel Nederland"
          intro="Van particuliere verhuizingen en transport voor bedrijven tot logistiek bij grote evenementen."
          center
        />
        <div className="mt-10">
          <img
            src={verhuisliftFlat.url}
            alt="Verhuiswagen en verhuislift van Vakkundig bij een flat, met verhuisdekens klaar voor gebruik"
            className="aspect-[16/9] w-full rounded-xl object-cover shadow-[var(--shadow-card)]"
            loading="lazy"
          />
        </div>
      </Section>




      <Section tone="muted">
        <SectionHeading
          eyebrow="Beoordeeld door onze klanten"
          title="507 reviews op Google, Trustoo en VerhuisOffertes"
          intro="Onze klanten laten hun ervaring achter op onafhankelijke platformen. Je kunt elke review bij de bron nalezen."
        />
        <div className="mt-8">
          <ReviewPlatforms />
        </div>
        <div className="mt-10">
          <ReviewsBlock limit={6} />
        </div>
        <div className="mt-8">
          <Link to="/reviews" className="font-bold text-brand hover:underline">
            Alle reviews bekijken →
          </Link>
        </div>
      </Section>


      <Section tone="sand">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading
              eyebrow="Waarom Vakkundig"
              title="Vijf redenen waarom klanten voor ons kiezen"
            />
            <ul className="mt-7 space-y-4">
              {usps.map((usp) => (
                <li key={usp} className="flex gap-3">
                  <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-brand text-brand-foreground">
                    <Check className="size-3.5" aria-hidden />
                  </span>
                  <span className="text-secondary">{usp}</span>
                </li>
              ))}
            </ul>
          </div>
          <img
            src={vlootWagens.url}
            alt="Wagenpark van Vakkundig Verhuizingen & Transport met bakwagens op rij"
            className="aspect-[4/3] w-full rounded-2xl object-cover shadow-[var(--shadow-card)]"
            loading="lazy"
          />
        </div>
      </Section>

      <Section tone="muted">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
          <video
            src={homeVideo2.url}
            poster={homeVideo2Poster.url}
            controls
            playsInline
            preload="none"
            aria-label="Video van een verhuizing door Vakkundig Verhuizingen & Transport"
            className="aspect-[9/16] w-full rounded-2xl bg-black object-cover shadow-[var(--shadow-card)] sm:aspect-video"
          />
          <div>
            <SectionHeading
              eyebrow="Op locatie"
              title="Vakkundig aan het werk"
              intro="Efficiënt inladen, alles beschermd met wikkelfolie en verhuisdekens en met oog voor detail geplaatst op de nieuwe locatie."
            />
          </div>
        </div>
      </Section>

      <Section>
        <div className="grid gap-8 md:grid-cols-3">
          {[
            {
              icon: Truck,
              title: "Eigen wagenpark en verhuislift",
              text: "Bakwagens tot 22 m³ met laadklep en een verhuislift voor smalle trappenhuizen en hoge verdiepingen.",
            },
            {
              icon: ShieldCheck,
              title: "Verzekerd en erkend",
              text: `Wij vervoeren verzekerd en werken met een ${site.permit}. KvK ${site.kvk}.`,
            },
            {
              icon: MapPin,
              title: "Landelijk transport",
              text: "Wij verhuizen en transporteren door heel Nederland en naar het buitenland, met Zuid-Holland als dagelijkse basis.",
            },
          ].map((item) => (
            <div key={item.title} className="rounded-xl border border-border bg-background p-6">
              <item.icon className="size-8 text-brand" aria-hidden />
              <h3 className="mt-4 font-display text-lg font-bold text-secondary">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading
          eyebrow="Werkgebied"
          title={`Verhuisbedrijf in ${regions.length} plaatsen in heel Nederland`}
          intro="Van Zuid-Holland tot Groningen, Brabant en Limburg: wij verhuizen dagelijks in Haaglanden, Rotterdam-Rijnmond, de Leidse regio en Bollenstreek, het Groene Hart, de Drechtsteden, Amsterdam en Noord-Holland, Utrecht, Flevoland, Brabant, Gelderland en Overijssel, het noorden, Limburg en Zeeland. Staat jouw plaats er niet bij? Bel ons, we rijden door heel Nederland."
        />
        <div className="mt-8 space-y-4 rounded-xl border border-border bg-background p-4">
          {regionsByGroup.map(({ group, items }) => (
            <details key={group} open className="group">
              <summary className="cursor-pointer text-left font-display font-bold text-secondary">
                {group}{" "}
                <span className="ml-1 text-xs font-semibold text-muted-foreground">({items.length})</span>
              </summary>
              <div className="mt-3 flex flex-wrap gap-2 pb-2">
                {items.map((region) => (
                  <Link
                    key={region.slug}
                    to="/werkgebied/$slug"
                    params={{ slug: region.slug }}
                    className="rounded-full border border-border px-3 py-1 text-xs font-semibold text-secondary transition hover:border-brand hover:text-brand"
                  >
                    {region.city}
                  </Link>
                ))}
              </div>
            </details>
          ))}
        </div>


        <Link to="/werkgebied" className="mt-8 inline-block font-bold text-brand hover:underline">
          Volledig werkgebied bekijken →
        </Link>
      </Section>

      <Section tone="sand">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
          <img
            src={liftAppartement.url}
            alt="Verhuiswagen van Vakkundig met uitgeschoven verhuislift langs een appartementencomplex"
            className="aspect-square w-full rounded-2xl object-cover shadow-[var(--shadow-card)] sm:aspect-[4/3]"
            loading="lazy"
          />
          <div>
            <SectionHeading
              eyebrow="Ook op hoogte"
              title="Verhuislift met ervaren bediening"
              intro="Snel en veilig verhuizen via raam of balkon, zonder schade aan het trappenhuis. Onze verhuislift is ook los te boeken."
            />
            <Link
              to="/diensten/$slug"
              params={{ slug: "verhuislift" }}
              className="mt-6 inline-block rounded-md bg-brand px-5 py-3 font-bold text-brand-foreground transition hover:bg-brand-dark"
            >
              Bekijk onze verhuisliftservice
            </Link>
          </div>
        </div>
      </Section>


      <Section tone="navy">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading
              eyebrow="Zo werken wij"
              title="Bekijk onze verhuizers in actie"
              intro="Een korte impressie van een dag op locatie: strak inladen, alles beschermd en netjes afgeleverd."
              inverted
            />
          </div>
          <video
            src={homeVideo.url}
            poster={homeVideoPoster.url}
            controls
            playsInline
            preload="none"
            aria-label="Video van een verhuizing door Vakkundig Verhuizingen & Transport"
            className="aspect-[9/16] w-full rounded-2xl bg-black object-cover shadow-[var(--shadow-card)] sm:aspect-video"
          />
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Veelgestelde vragen" title="Antwoord op de vragen die we vaak krijgen" />
        <div className="mt-8">
          <FaqBlock />
        </div>
      </Section>

      <CtaBand />
    </>
  );
}
